import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  autoriserMemoireArrivee, oublierMemoireArrivee,
} from '../lib/mesure';
import {
  appliquerConsentementPublicite, declarerRefusParDefaut, publiciteConfiguree,
} from '../lib/pixels';

/**
 * Consentement du visiteur.
 *
 * Deux réglages facultatifs seulement, parce que la mesure d'audience du site
 * se fait sans rien déposer sur l'appareil et n'a donc pas à être soumise à
 * un accord. La rubrique « publicité » n'apparaît que si un pixel est
 * réellement configuré : pas de case à cocher pour quelque chose qui
 * n'existe pas.
 *
 * Le choix est conservé dans le stockage local — ce qui est permis sans
 * accord, puisqu'il est nécessaire pour respecter la décision prise. Le
 * numéro de version fait reposer la question si la politique change : un
 * accord donné sur un texte ne vaut pas pour un autre.
 */

const VERSION_POLITIQUE = '1';
const CLE = 'afrione_consentement';

const REFUS = { mesure: false, publicite: false };

const ConsentContext = createContext(null);

function lireChoix() {
  try {
    const brut = localStorage.getItem(CLE);
    if (!brut) return null;
    const choix = JSON.parse(brut);
    if (choix?.version !== VERSION_POLITIQUE) return null;
    return { mesure: Boolean(choix.mesure), publicite: Boolean(choix.publicite), id: choix.id };
  } catch (e) {
    return null;
  }
}

function identifiant() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (e) {
    // Repli ci-dessous.
  }
  return `c${Date.now()}${Math.random().toString(36).slice(2, 10)}`;
}

export function ConsentProvider({ children }) {
  const [choix, setChoix] = useState(REFUS);
  const [demande, setDemande] = useState(false);   // bannière visible
  const [reglages, setReglages] = useState(false); // panneau détaillé visible

  // Appliquer un choix : les effets de bord vivent ici, en un seul endroit.
  const appliquer = useCallback((valeurs) => {
    if (valeurs.mesure) autoriserMemoireArrivee();
    else oublierMemoireArrivee();
    appliquerConsentementPublicite(Boolean(valeurs.publicite) && publiciteConfiguree);
  }, []);

  useEffect(() => {
    // Le refus doit être déclaré avant tout chargement de balise Google.
    declarerRefusParDefaut();
    const connu = lireChoix();
    if (connu) {
      setChoix(connu);
      appliquer(connu);
    } else {
      setDemande(true);
    }
  }, [appliquer]);

  const enregistrer = useCallback((valeurs) => {
    const complet = {
      mesure: Boolean(valeurs.mesure),
      publicite: Boolean(valeurs.publicite),
    };
    const precedent = lireChoix();
    const id = precedent?.id || identifiant();

    setChoix(complet);
    setDemande(false);
    setReglages(false);
    appliquer(complet);

    try {
      localStorage.setItem(CLE, JSON.stringify({
        ...complet, id, version: VERSION_POLITIQUE, date: new Date().toISOString(),
      }));
    } catch (e) {
      // Stockage refusé : le choix vaut pour cette visite seulement, et la
      // question sera reposée la prochaine fois. C'est le comportement sûr.
    }

    // Preuve conservée côté serveur : sans historique, impossible de montrer
    // ce qui était autorisé à une date donnée. Un échec reste sans
    // conséquence pour le visiteur.
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/consentement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...complet, version: VERSION_POLITIQUE }),
        keepalive: true,
      }).catch(() => {});
    } catch (e) {
      // Sans effet sur la navigation.
    }
  }, [appliquer]);

  const valeur = {
    choix,
    demande,
    reglages,
    publiciteConfiguree,
    enregistrer,
    toutAccepter: () => enregistrer({ mesure: true, publicite: true }),
    toutRefuser: () => enregistrer(REFUS),
    ouvrirReglages: () => { setReglages(true); setDemande(true); },
    fermerReglages: () => setReglages(false),
  };

  return <ConsentContext.Provider value={valeur}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const contexte = useContext(ConsentContext);
  if (!contexte) throw new Error('useConsent doit être utilisé dans un ConsentProvider');
  return contexte;
}
